/**
 * Pure helpers for invite creation — the contract between the (display-shaped)
 * invite form and the (strict) backends.
 *
 * Two production bugs lived in this seam:
 *  1. Unit-less departments (องค์กรแพทย์ / องค์การพยาบาล) sent unit='' which
 *     violates the invites_unit_id_fkey foreign key. NULL is the valid "no ward".
 *  2. The form passed a human expiry label ('in 24 hours') that Date.parse()
 *     turned into NaN, so create_invite's p_ttl always defaulted to 7 days.
 */

/** Selectable invite lifetimes. The form's segmented control emits these codes. */
export const EXPIRY_CODES = ['24h', '7d', '30d'] as const;
export type ExpiryCode = (typeof EXPIRY_CODES)[number];

const TTL_BY_CODE: Record<ExpiryCode, string> = {
  '24h': '24 hours',
  '7d': '7 days',
  '30d': '30 days',
};

const LABEL_BY_CODE: Record<ExpiryCode, string> = {
  '24h': 'in 24 hours',
  '7d': 'in 7 days',
  '30d': 'in 30 days',
};

/** Map an expiry code to a Postgres interval string for create_invite(p_ttl). */
export function ttlFromExpiry(code: ExpiryCode): string {
  return TTL_BY_CODE[code] ?? '7 days';
}

/** Human-friendly label for the mock repository / display. */
export function expiryLabel(code: ExpiryCode): string {
  return LABEL_BY_CODE[code] ?? 'in 7 days';
}

/**
 * Normalize a unit selection to an FK-safe value. An empty/absent unit means
 * "whole organization" — it must be NULL, never '' (which has no units.id row).
 */
export function normalizeUnit(unitId: string | null | undefined): string | null {
  return unitId ? unitId : null;
}
